import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { View, Platform } from 'react-native'
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js'
import { Button } from 'react-native-paper'
import * as Sentry from '@sentry/react-native'
import Toast from 'react-native-root-toast'
import { Env } from '../../../env'
import { MelddModal } from '../../../@ui/containers/modals'

type PaypalButtonsComponentProps = {
  visible: boolean
  onDismiss: () => void
  onCreateOrder: () => Promise<string>
  onApprove: (order: any) => Promise<void>
  onError?: (error: any) => void
  title: string
  successMessage?: string
  errorMessage?: string
  cancelLabel?: string
}

/**
 * Shared PayPal buttons component for creating and capturing orders
 * Used by both payment flow and vault setup flow
 */
export const PaypalButtonsComponent = ({
  visible,
  onDismiss,
  onCreateOrder,
  onApprove,
  onError,
  title,
  successMessage,
  errorMessage,
  cancelLabel,
}: PaypalButtonsComponentProps) => {
  const { t: tPayment } = useTranslation('payment')
  const [loading, setLoading] = useState(false)

  if (!Env.PAYPAL_CLIENT_ID) {
    return null
  }

  // Only show PayPal on web platform (PayPal SDK is web-based)
  if (Platform.OS !== 'web') {
    return null
  }

  const handleClose = () => {
    setLoading(false)
    onDismiss()
  }

  const handlePaypalApprove = async (data: any, actions: any) => {
    try {
      const order = await actions.order.capture()
      setLoading(false)
      if (successMessage) {
        Toast.show(successMessage, {
          position: Toast.positions.CENTER,
        })
      }
      // Wait for onApprove to complete before closing modal
      // This allows components to show loading/checking states
      await onApprove(order)
      handleClose()
    } catch (error: any) {
      console.error('PayPal approval error:', error)
      Sentry.captureException(error)
      const message =
        error.message || errorMessage || tPayment('paypal-processing-failed')
      Toast.show(message, {
        position: Toast.positions.CENTER,
      })
      setLoading(false)
      if (onError) {
        onError(error)
      }
    }
  }

  const handlePaypalError = (err: any) => {
    console.error('PayPal error:', err)
    Sentry.captureException(err, {
      extra: {
        err,
        paypalErrorDetails: {
          message: err.message,
          details: err.details,
          response: err.response,
          timestamp: err.timestamp,
        },
      },
    })
    // Extract more detailed error message
    const errorMessageText =
      err.details?.message ||
      err.message ||
      errorMessage ||
      tPayment('paypal-error-occurred')
    Toast.show(errorMessageText, {
      position: Toast.positions.CENTER,
    })
    setLoading(false)
    if (onError) {
      onError(err)
    }
  }

  const createPaypalOrder = async (): Promise<string> => {
    setLoading(true)
    try {
      const orderId = await onCreateOrder()
      setLoading(false)
      return orderId
    } catch (error: any) {
      console.error('PayPal order creation error:', error)
      Sentry.captureException(error, {
        extra: {
          error,
          graphQLErrors: error.graphQLErrors,
          networkError: error.networkError,
        },
      })
      // Extract more detailed error message from GraphQL or network errors
      const errorMessageText =
        error.graphQLErrors?.[0]?.message ||
        error.networkError?.result?.errors?.[0]?.message ||
        error.message ||
        errorMessage ||
        tPayment('paypal-order-creation-failed')
      Toast.show(errorMessageText, {
        position: Toast.positions.CENTER,
      })
      setLoading(false)
      throw error
    }
  }

  return (
    <MelddModal
      visible={visible}
      onDismiss={handleClose}
      title={title}
      body={
        <View
          style={{
            minHeight: 200,
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {Env.PAYPAL_CLIENT_ID && (
            <PayPalScriptProvider
              options={{
                clientId: Env.PAYPAL_CLIENT_ID,
              }}
            >
              <PayPalButtons
                createOrder={createPaypalOrder}
                onApprove={handlePaypalApprove}
                onError={handlePaypalError}
                onCancel={handleClose}
                style={{
                  layout: 'vertical',
                  color: 'blue',
                  shape: 'rect',
                  label: 'pay',
                }}
              />
            </PayPalScriptProvider>
          )}
        </View>
      }
      actions={
        <Button onPress={handleClose} disabled={loading}>
          {cancelLabel || tPayment('cancel')}
        </Button>
      }
    />
  )
}
